import React, { useState, useEffect, useCallback } from "react";
import { Select } from "antd";
import { list as getAdminList } from "../controllers/subAdmin/subAdminControllers";

/**
 * Reusable Admin Select Component with Search and Select All functionality
 * 
 * @param {Array|String} value - Selected admin ID(s) - Array for multiple, String for single
 * @param {Function} onChange - Callback function when selection changes
 * @param {String} placeholder - Placeholder text (default: "Select Admins")
 * @param {String} className - CSS class name
 * @param {Object} style - Inline styles
 * @param {Number} maxTagCount - Maximum number of tags to show (default: 3)
 * @param {Boolean} showSearch - Enable search functionality (default: true)
 * @param {String} mode - Select mode: "multiple", "tags", etc. (default: "tags")
 * @param {Object} condition - Additional conditions for admin filtering
 * @param {Boolean} multiple - Enable multiple selection (default: true). Set to false for single selection
 * @param {Boolean} disableOptions - Disable options without device_id (default: true)
 * @param {Boolean} showSelectAll - Show "Select All" option (default: true, only works when multiple=true)
 */
const AdminSelectWithSearch = ({
  value,
  onChange,
  placeholder = "Select Admins",
  className = "form-control store_input_field",
  style = { width: '100%' },
  maxTagCount = 3,
  showSearch = true,
  mode = "tags",
  condition = {},
  disabled = false,
  disableOptions = true,
  multiple = true,
  showSelectAll = true,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch admin data
  const getAdminData = useCallback(async () => {
    try {
      setLoading(true);
      const requestOptions = {
        type: "",
        condition: {
          status: "A",
          admin_type: "Sub Admin",
          ...condition,
        },
        sort: { admin_first_name: 1, admin_last_name: 1 },
        select: { 
          admin_first_name: 1, 
          admin_last_name: 1, 
          admin_phone: 1, 
          admin_email: 1,
          web_device_id: 1,
        },
        limit: 1000,
        skip: 0,
      };
      
      const res = await getAdminList(requestOptions);
      
      if (res?.status === true) {
        const adminOptions = res?.result?.map((item) => ({
          label: `${item?.admin_first_name?.toUpperCase()} ${item?.admin_last_name?.toUpperCase()} (${item?.admin_phone})`,
          value: item?._id,
          ...(disableOptions ? {disabled: item?.web_device_id ? false : true} : {}),
          admin_first_name: item?.admin_first_name,
          admin_last_name: item?.admin_last_name,
          web_device_id: item?.web_device_id,
          admin_phone: item?.admin_phone,
          admin_email: item?.admin_email,
        }));
        // Add "Select All" option at the beginning (only for multiple mode)
        if (multiple && showSelectAll) {
          setOptions([
            {
              label: "SELECT ALL",
              value: "select_all",
              disabled: false,
              admin_first_name: "SELECT ALL",
              admin_last_name: "",
              admin_phone: "",
              admin_email: "",
            },
            ...adminOptions,
          ]);
        } else {
          setOptions(adminOptions);
        }
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching admins: ", error);
      setLoading(false);
    }
  }, [condition, disableOptions, multiple, showSelectAll]);

  // Load admins on mount and when relevant props change
  useEffect(() => {
    getAdminData();
  }, []);

  // Handle selection change
  const handleChange = (selectedValues) => {
    try {
      // Single selection mode - just pass the value directly
      if (!multiple) {
        onChange && onChange(selectedValues);
        return;
      }

      // Multiple selection mode with Select All functionality
      const currentValue = value || [];
      const allAdminIds = options
        .filter((option) => option.value !== "select_all" && !option.disabled)
        .map((option) => option.value);

      // Check if 'select_all' is in the new value
      const hasSelectAll = selectedValues.includes("select_all");

      // Check if all admins were previously selected
      const wasAllSelected = currentValue.length === allAdminIds.length && allAdminIds.length > 0;

      // Remove 'select_all' from value to get actual admin selections
      const filteredValue = selectedValues.filter((v) => v !== "select_all");

      // Scenario 1: Admin clicked 'Select All' from dropdown
      if (hasSelectAll && !wasAllSelected) {
        onChange && onChange(allAdminIds);
      }
      // Scenario 2: Admin removed the 'Select All' tag
      else if (wasAllSelected && !hasSelectAll && filteredValue.length === 0) {
        onChange && onChange([]);
      }
      // Scenario 3: Normal operation - admin is adding/removing individual admins
      else {
        onChange && onChange(filteredValue);
      }
    } catch (error) {
      console.error("Error handling change: ", error);
    }
  };

  // Calculate display value
  const displayValue = (() => {
    // Single selection mode - return value as is
    if (!multiple) {
      return value || undefined;
    }

    // Multiple selection mode - handle Select All
    const currentValue = value || [];
    const allAdminIds = options
      .filter((option) => option.value !== "select_all" && !option.disabled)
      .map((option) => option.value);
    
    // Show "Select All" if all admins are selected
    if (showSelectAll && currentValue.length === allAdminIds.length && allAdminIds.length > 0) {
      return ["select_all", ...currentValue];
    }
    return currentValue;
  })();

  return (
    <Select
      className={className}
      mode={multiple ? mode : undefined}
      style={style}
      placeholder={placeholder}
      value={displayValue}
      onChange={handleChange}
      options={options}
      showSearch={showSearch}
      loading={loading}
      disabled={disabled}
      maxTagCount={multiple ? maxTagCount : undefined}
      maxTagPlaceholder={multiple ? (omittedValues) => `+${omittedValues.length}` : undefined}
      dropdownStyle={{ zIndex: 10000 }}
      getPopupContainer={(trigger) => trigger.parentNode}
      filterOption={(input, option) => {
        const searchValue = input.toLowerCase();
        return (
          option.admin_first_name?.toLowerCase()?.includes(searchValue) ||
          option.admin_last_name?.toLowerCase()?.includes(searchValue) ||
          option.admin_phone?.toString()?.toLowerCase()?.includes(searchValue) ||
          option.admin_email?.toLowerCase()?.includes(searchValue) ||
          option.label?.toLowerCase()?.includes(searchValue)
        );
      }}
      optionRender={(option) => {
        if (option.data.value === "select_all") {
          return (
            <div style={{ fontWeight: 600, color: "#1890ff", padding: "5px 0" }}>
              {option.data.admin_first_name}
            </div>
          );
        }
        return (
          <div>
            <div style={{ fontWeight: 600 }}>
              {option.data.admin_first_name?.toUpperCase()} {option.data.admin_last_name?.toUpperCase()}
            </div>
            {option.data.admin_phone && (
              <div style={{ fontSize: 12, color: "#666" }}>
                {option.data.admin_phone}
              </div>
            )}
            {option.data.admin_email && (
              <div style={{ fontSize: 12, color: "#666" }}>
                {option.data.admin_email}
              </div>
            )}
          </div>
        );
      }}
    />
  );
};

export default AdminSelectWithSearch;

